import { type IPanelGeneratedModelDto, PanelTypeGenerationStatus } from "../../../responses/panelGeneratedModelDto";
import {
    PANEL_TYPE_MODELS,
    type PanelSourceModelType,
    type PanelSourceUnion,
} from "../../../responses/typesManagerPanelTypes";
import type { ICornerPanelSource } from "../../../responses/cornerPanelSource";
import type { ICustomCornerSource } from "../../../responses/customCornerSource";
import type { ICustomPanelSource } from "../../../responses/customPanelSource";
import type { ICustomZShapedPanelSource } from "../../../responses/customZShapedPanelSource";
import type { IHookSource } from "../../../responses/hookSource";
import type { IPanelGeneratedDrawingDto } from "../../../responses/panelGeneratedDrawingDto";
import type { IPanelSource } from "../../../responses/panelSource";
import type { IPanelLouvreCutouts, IPanelWindowDesignParameters } from "../../../responses/panelType";
import type { Corner } from "./corner";
import type { CustomCorner } from "./customCorner";
import type { CustomPanel } from "./customPanel";
import type { CustomZShapedPanel } from "./customZShapedPanel";
import type { Panel } from "./panel";

type PanelsType<T> = T extends IPanelSource
    ? Panel
    : T extends ICornerPanelSource
        ? Corner
        : T extends ICustomPanelSource
            ? CustomPanel
            : T extends ICustomCornerSource
                ? CustomCorner
                : T extends ICustomZShapedPanelSource
                    ? CustomZShapedPanel
                    : never;

type PanelSource<T> = PanelSourceModelType<T>;

export class PanelType<T extends PanelSourceUnion> {
    private readonly _panels = new Map<string, PanelsType<T>>();
    private readonly _panelSource: PanelSource<T>;
    private readonly _windowsDesignParameters: IPanelWindowDesignParameters[];
    private readonly _louvreCutouts: IPanelLouvreCutouts[];
    private readonly isRegularType: boolean;
    private _withoutCladdings: boolean;
    private _generatedModel: IPanelGeneratedModelDto | null = null;
    private _generatedDrawings: IPanelGeneratedDrawingDto[] | null = null;
    private _name: string;
    private _isSelected: boolean;

    constructor(panelSource: PanelSource<T>, isConvertedPanelType = false) {
        this._panelSource = panelSource;
        this.id = panelSource.id;
        this._name = panelSource.name;
        this._isSelected = false;
        const isRegularType = !isConvertedPanelType && "hooks" in panelSource;
        this.isRegularType = isRegularType;
        this.hooks = isRegularType ? [...panelSource.hooks] : [];
        this._withoutCladdings = "withoutCladdings" in panelSource ? panelSource.withoutCladdings : false;
        this._windowsDesignParameters =
            "windowsDesignParameters" in panelSource ? panelSource.windowsDesignParameters : [];
        this._louvreCutouts = "louvreCutouts" in panelSource ? panelSource.louvreCutouts : [];
    }

    readonly id: string;
    readonly hooks: IHookSource[];

    get name(): string {
        return this._name;
    }

    set name(newName: string) {
        if (this._name !== newName) {
            this._name = newName;
            this._generatedModel = null;
        }
    }

    get isSelected(): boolean {
        return this._isSelected;
    }

    set isSelected(value: boolean) {
        this._isSelected = value;
    }

    get panels(): PanelsType<T>[] {
        return Array.from(this._panels.values());
    }

    get panelSource(): PanelSource<T> {
        return this._panelSource;
    }

    get withoutCladdings(): boolean {
        return this._withoutCladdings;
    }

    set withoutCladdings(value: boolean) {
        if (this._withoutCladdings === value) {
            return;
        }
        this._withoutCladdings = value;
        for (const panel of this.panels) {
            panel.setPanelType(this, this.isRegularType);
        }

        this._generatedModel = null;
    }

    get windowsDesignParameters(): IPanelWindowDesignParameters[] {
        return this._windowsDesignParameters;
    }

    get louvreCutouts(): IPanelLouvreCutouts[] {
        return this._louvreCutouts;
    }

    get generatedModel(): IPanelGeneratedModelDto | null {
        return this._generatedModel;
    }

    set generatedModel(value: IPanelGeneratedModelDto | null) {
        this._generatedModel = value;
    }

    get generatedDrawings(): IPanelGeneratedDrawingDto[] | null {
        return this._generatedDrawings;
    }

    get status(): PanelTypeGenerationStatus {
        if (this.isRegularType) {
            return this._generatedModel?.status ?? PanelTypeGenerationStatus.None;
        }
        return PanelTypeGenerationStatus.ModelGeneratedSuccessfully;
    }

    addPanel(panel: PanelsType<T>) {
        panel.setPanelType(this, this.isRegularType);
        this._panels.set(panel.id, panel);
    }

    removePanel(panel: PanelsType<T>) {
        this._panels.delete(panel.id);
    }

    addHook(hook: IHookSource, index: number) {
        this.hooks.splice(index, 0, hook);

        this._generatedModel = null;
    }

    updateHook(index: number, hook: IHookSource) {
        const panelHook = this.hooks[index];

        panelHook.x = hook.x;
        panelHook.y = hook.y;
        panelHook.hookType = hook.hookType;
        panelHook.profileSide = hook.profileSide;
        panelHook.withPin = hook.withPin;

        this._generatedModel = null;
    }

    removeHook(index: number) {
        this.hooks.splice(index, 1);

        this._generatedModel = null;
    }

    setPanelWindowDesignParameters(index: number, parameters: IPanelWindowDesignParameters) {
        const currentParameters = this._windowsDesignParameters[index];
        if (!currentParameters) {
            throw new Error(`Index ${index} is out of range!`);
        }

        // TODO: DDSC-3072 Currently, the backend does not check parameter equality, and this
        // method is called after the parameters are updated, so the backend will reset the
        // generated model even if the parameters are unchanged. This is partially guarded by
        // the UI of the window properties docking panel, but that is not enough on its own.
        // Once proper equality checking is added, uncomment the code below to avoid
        // unnecessary resets of the generated model:
        // const areParametersEqual = isAlmostEqual(currentParameters.area, parameters.area)
        //     && isAlmostEqual(currentParameters.weight, parameters.weight);

        // if (areParametersEqual) {
        //     return;
        // }

        for (const panel of this.panels) {
            panel.setPanelWindowDesignParameters(index, parameters);
        }

        this._windowsDesignParameters[index] = parameters;

        this._generatedModel = null;
    }

    setLouvreCutouts(index: number, cutouts: IPanelLouvreCutouts) {
        const currentCutouts = this._louvreCutouts[index];
        if (!currentCutouts) {
            throw new Error(`Index ${index} is out of range!`);
        }

        // TODO: DDSC-3072 add proper equality checking for louvre cutouts

        for (const panel of this.panels) {
            panel.setLouvreCutouts(index, cutouts);
        }

        this._louvreCutouts[index] = cutouts;

        this._generatedModel = null;
    }

    setGeneratedDrawings(generatedDrawings: IPanelGeneratedDrawingDto[]) {
        this._generatedDrawings = generatedDrawings;
    }

    setInProgress() {
        this.generatedModel = PANEL_TYPE_MODELS.inProgress;
    }

    setFailed() {
        this.generatedModel = PANEL_TYPE_MODELS.failed;
    }
}
